/* ===================================================================
   Templates - craft review-request messages. WIRED (2026-06-17, Brad) to
   GET/POST/PUT/DELETE /api/v1/templates. The list refetches after every
   write (create / edit / delete). A composer modal handles new + edit.

   HARD PRODUCT RULE - nothing here sends anything. Templates are message
   bodies only; sending happens (dry-run) from the Requests flow.

   PRESENTATION (2026-07-03, Corbato): pixel-faithful re-skin to the Claude
   Design "Rhody Reviews.dc.html" TEMPLATES screen (desktop 1027-1089;
   mobile 2309-2357). Cards are grouped by funnel STAGE; each carries a
   channel badge (SMS/Email), an optional DEFAULT marker, an optional TOP
   PERFORMER marker, and Sent + "Left a review" conversion stats. CRUD +
   default + gallery wiring are unchanged -- only the markup/styling moved.
   =================================================================== */
import { useState } from 'react';
import { Icon, ClosePath, EmptyState } from '../components/ui';
import { useTemplates, useTemplateMutations } from '../hooks';
import type { Template, TemplateInput } from '../lib/api';
import { TemplateGallery } from '../components/TemplateGallery';
import '../styles/components.css';
import '../styles/templates.css';

const PlusPath = <path d="M12 5v14M5 12h14" strokeLinecap="round" />;
const GridPath = (
  <>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
  </>
);

/* Star glyph for the TOP PERFORMER badge (verbatim path from the mockup). */
const StarGlyph = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2l2.9 6.3 6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 20.4l1.5-6.8L2.2 9l6.9-.7z" />
  </svg>
);

/* ---- Funnel-stage grouping ----------------------------------------------
   The tenant Template model has no `stage` field, so the prototype's
   "grouped by when they're sent" is derived from the template name/scenario
   keywords. Every template lands in exactly one group; the fallthrough is
   "Other requests" so nothing is ever dropped. Order follows the funnel. */
type StageKey = 'after_job' | 'reminder' | 'win_back' | 'other';
const STAGE_ORDER: StageKey[] = ['after_job', 'reminder', 'win_back', 'other'];
const STAGE_META: Record<StageKey, { label: string; desc: string }> = {
  after_job: {
    label: 'Right after the job',
    desc: 'Sent while the work is fresh. Your highest-converting moment.',
  },
  reminder: {
    label: 'Follow-up reminders',
    desc: 'A gentle nudge for customers who did not respond the first time.',
  },
  win_back: {
    label: 'Win-back & repeat',
    desc: 'Re-engage past customers and ask repeat clients for a fresh review.',
  },
  other: {
    label: 'Other requests',
    desc: 'Everything else in your library.',
  },
};

function stageOf(tpl: Template): StageKey {
  const hay = `${tpl.name} ${String((tpl as Record<string, unknown>).scenario ?? '')}`.toLowerCase();
  if (/remind|follow.?up|nudge|2nd|second|day 3|3.?day/.test(hay)) return 'reminder';
  if (/win.?back|repeat|return|again|past|seasonal/.test(hay)) return 'win_back';
  if (/after.?job|after.?service|first.?time|thank|just finished|complete|done|estimate/.test(hay))
    return 'after_job';
  return 'other';
}

/* Conversion ("Left a review") -- only shown when the backend actually
   supplies it. Never fabricated. Reads reviewed_count / converted / a
   0..1 conv_rate off the index-signature fields if present. */
function conversion(tpl: Template): { pct: number; label: string } | null {
  const rec = tpl as Record<string, unknown>;
  const sent = typeof tpl.sent_count === 'number' ? tpl.sent_count : 0;
  const reviewed =
    typeof rec.reviewed_count === 'number'
      ? (rec.reviewed_count as number)
      : typeof rec.converted === 'number'
        ? (rec.converted as number)
        : null;
  if (reviewed != null && sent > 0) {
    const pct = Math.round((reviewed / sent) * 100);
    return { pct, label: `${pct}%` };
  }
  if (typeof rec.conv_rate === 'number') {
    const pct = Math.round((rec.conv_rate as number) * 100);
    return { pct, label: `${pct}%` };
  }
  return null;
}

function channelLabel(channel: string): string {
  return channel === 'sms' ? 'SMS' : 'Email';
}

function TemplateCard({
  tpl,
  isTop,
  onEdit,
  onDelete,
  deleting,
}: {
  tpl: Template;
  isTop: boolean;
  onEdit: (t: Template) => void;
  onDelete: (t: Template) => void;
  deleting: boolean;
}) {
  const isSms = tpl.channel === 'sms';
  const conv = conversion(tpl);
  const sent = tpl.sent_count ?? 0;
  return (
    <div className="tpl-card">
      <div className="tpl-card__top">
        <div className="tpl-card__title">{tpl.name}</div>
        <div className="tpl-card__badges">
          {isTop && (
            <span className="tpl-badge tpl-badge--top">
              {StarGlyph}
              TOP PERFORMER
            </span>
          )}
          {tpl.is_default && <span className="tpl-badge tpl-badge--default">DEFAULT</span>}
          <span
            className={
              'tpl-badge ' + (isSms ? 'tpl-badge--channel-sms' : 'tpl-badge--channel-email')
            }
          >
            <span className="tpl-badge__dot" />
            {channelLabel(tpl.channel)}
          </span>
        </div>
      </div>

      {isSms ? (
        <div className="tpl-bubble--sms">{tpl.body}</div>
      ) : (
        <div className="tpl-bubble--email">{tpl.body}</div>
      )}

      <div className="tpl-card__foot">
        {/* Desktop: two stat tiles. */}
        <div className="tpl-stats">
          <div>
            <div className="tpl-stat__num">{sent}</div>
            <div className="tpl-stat__lab">SENT</div>
          </div>
          {conv && (
            <div>
              <div className="tpl-stat__num tpl-stat__num--conv">{conv.label}</div>
              <div className="tpl-stat__lab">LEFT A REVIEW</div>
            </div>
          )}
        </div>
        {/* Phone: collapsed single stat line (mockup mobile). */}
        <div className="tpl-statline--mobile">
          <span className="dot" />
          {sent} sent{conv ? ` · ${conv.label} reviewed` : ''}
        </div>

        <div className="tpl-card__actions">
          <button className="tpl-mini tpl-mini--edit" onClick={() => onEdit(tpl)}>
            Edit
          </button>
          <button
            className="tpl-mini tpl-mini--del"
            onClick={() => onDelete(tpl)}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

type Editing = { mode: 'new' } | { mode: 'edit'; tpl: Template };

function TemplateModal({
  editing,
  onClose,
  onSave,
  saving,
  error,
}: {
  editing: Editing;
  onClose: () => void;
  onSave: (input: TemplateInput) => void;
  saving: boolean;
  error: Error | null;
}) {
  const existing = editing.mode === 'edit' ? editing.tpl : null;
  const [name, setName] = useState(existing?.name ?? '');
  const [channel, setChannel] = useState<'sms' | 'email'>(
    (existing?.channel as 'sms' | 'email') ?? 'sms',
  );
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [isDefault, setIsDefault] = useState(existing?.is_default ?? false);

  const valid = name.trim() && body.trim();

  function submit() {
    if (!valid) return;
    onSave({
      name: name.trim(),
      channel,
      body: body.trim(),
      subject: channel === 'email' ? subject.trim() || null : null,
      is_default: isDefault,
    });
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal__head">
          <h3>{editing.mode === 'edit' ? 'Edit template' : 'New template'}</h3>
          <button className="x" onClick={onClose} aria-label="Close"><Icon d={ClosePath} /></button>
        </div>
        <div className="modal__body">
          <div className="formgrid">
            <label className="fld">
              <span className="lab">Name</span>
              <input className="inp" value={name} placeholder="e.g. After-job - friendly" onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="fld">
              <span className="lab">Channel</span>
              <select className="sel" value={channel} onChange={(e) => setChannel(e.target.value as 'sms' | 'email')}>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
            </label>
            {channel === 'email' && (
              <label className="fld full">
                <span className="lab">Subject</span>
                <input className="inp" value={subject ?? ''} placeholder="e.g. How did we do?" onChange={(e) => setSubject(e.target.value)} />
              </label>
            )}
            <label className="fld full">
              <span className="lab">Message</span>
              <textarea className="inp" rows={4} value={body} placeholder="Hi {{first_name}} - thanks! Would you share a quick review? {{link}}" onChange={(e) => setBody(e.target.value)} />
            </label>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Make this the default template
            </label>
          </div>
          {error && (
            <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>
              Could not save the template. Please check the fields and try again.
            </p>
          )}
        </div>
        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving...' : 'Save template'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Group templates into ordered funnel stages, default pinned first inside
   each group, then best conversion, then most sent. Compute the single
   TOP PERFORMER per group (highest conversion, only when >1 item AND a real
   conversion figure exists so we never crown on fabricated data). */
type Group = { key: StageKey; items: Template[]; topId: string | null };

function buildGroups(templates: Template[]): Group[] {
  const byStage = new Map<StageKey, Template[]>();
  for (const t of templates) {
    const k = stageOf(t);
    (byStage.get(k) ?? byStage.set(k, []).get(k)!).push(t);
  }
  const groups: Group[] = [];
  for (const key of STAGE_ORDER) {
    const items = byStage.get(key);
    if (!items || items.length === 0) continue;
    const sorted = [...items].sort((a, b) => {
      if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
      const ca = conversion(a)?.pct ?? -1;
      const cb = conversion(b)?.pct ?? -1;
      if (ca !== cb) return cb - ca;
      return (b.sent_count ?? 0) - (a.sent_count ?? 0);
    });
    // Top performer: best real conversion in a group of >1.
    let topId: string | null = null;
    if (sorted.length > 1) {
      let best: { id: string; pct: number } | null = null;
      for (const t of sorted) {
        const c = conversion(t);
        if (c && (best === null || c.pct > best.pct)) best = { id: t.id, pct: c.pct };
      }
      topId = best?.id ?? null;
    }
    groups.push({ key, items: sorted, topId });
  }
  return groups;
}

export function Templates() {
  const q = useTemplates();
  const mut = useTemplateMutations();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);

  const templates = q.data?.templates ?? [];
  const groups = buildGroups(templates);

  async function handleSave(input: TemplateInput) {
    try {
      if (editing?.mode === 'edit') await mut.update(editing.tpl.id, input);
      else await mut.create(input);
      setEditing(null);
    } catch {
      /* error surfaced in the modal */
    }
  }

  async function handleDelete(tpl: Template) {
    setDeletingId(tpl.id);
    setDeleteErr(null);
    try {
      await mut.remove(tpl.id);
    } catch {
      // Surface the failure instead of silently keeping the row. The delete
      // action lives on the card face, so there's no modal error to catch it.
      setDeleteErr(`Couldn't delete "${tpl.name}". Please try again.`);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="tpl-view">
      <div className="tpl-head">
        <div>
          <h1 className="tpl-head__title">Templates</h1>
          <p className="tpl-head__sub">The words you send. Craft and A/B-test your review requests.</p>
        </div>
        <div className="tpl-head__actions">
          <button className="tpl-btn tpl-btn--ghost" onClick={() => setBrowsing(true)}>
            <Icon d={GridPath} /> Browse library
          </button>
          <button className="tpl-btn tpl-btn--primary" onClick={() => setEditing({ mode: 'new' })}>
            <Icon d={PlusPath} sw={2.5} /> New template
          </button>
        </div>
      </div>

      <div className="tpl-body">
        {deleteErr && (
          <div
            role="alert"
            style={{
              margin: '0 0 14px',
              padding: '10px 14px',
              borderRadius: 10,
              background: '#fce8e6',
              color: '#c5221f',
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            {deleteErr}
          </div>
        )}
        {q.isLoading ? (
          <div className="tpl-empty">Loading your templates...</div>
        ) : templates.length === 0 ? (
          <EmptyState
            title="No templates yet"
            desc="Templates are the review-request messages Rhody Reviews sends. Create your first to get started."
            actionLabel="New template"
            onAction={() => setEditing({ mode: 'new' })}
          />
        ) : (
          <>
            <div className="tpl-intro">
              Your <strong>{templates.length}</strong> templates, grouped by when they're sent.
              Best performer sits first in each group; your default is pinned to the top.
            </div>
            {groups.map((group) => {
              const meta = STAGE_META[group.key];
              return (
                <div className="tpl-group" key={group.key}>
                  <div className="tpl-group__head">
                    <div className="tpl-group__label">{meta.label}</div>
                    <div className="tpl-group__count">{group.items.length}</div>
                  </div>
                  <div className="tpl-group__desc">{meta.desc}</div>
                  <div className="tpl-grid">
                    {group.items.map((t) => (
                      <TemplateCard
                        key={t.id}
                        tpl={t}
                        isTop={group.topId === t.id}
                        onEdit={(tpl) => setEditing({ mode: 'edit', tpl })}
                        onDelete={handleDelete}
                        deleting={deletingId === t.id}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {editing && (
        <TemplateModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={mut.isPending}
          error={mut.error}
        />
      )}

      {browsing && <TemplateGallery onClose={() => setBrowsing(false)} />}
    </div>
  );
}
